import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  fetchMe,
  fetchCsrf,
  login as apiLogin,
  logout as apiLogout,
  register as apiRegister,
  setCsrfToken,
  type ApiUser,
} from "@/lib/api";

interface AuthContextValue {
  user: ApiUser | null;
  loading: boolean;
  refresh: () => Promise<void>;
  login: (login: string, password: string) => Promise<void>;
  register: (login: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  canEdit: boolean;
  /** Просмотрщику (роль viewer) документы не показываются нигде. */
  canSeeDocuments: boolean;
  isAdmin: boolean;
  mustChangePassword: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<ApiUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      await fetchCsrf();
      const { user: u } = await fetchMe();
      setUser(u);
    } catch {
      setUser(null);
      setCsrfToken(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (loginStr: string, password: string) => {
    const { user: u, csrfToken } = await apiLogin(loginStr, password);
    if (csrfToken) setCsrfToken(csrfToken);
    setUser(u);
  }, []);

  const register = useCallback(async (loginStr: string, password: string) => {
    const { user: u, csrfToken } = await apiRegister(loginStr, password);
    if (csrfToken) setCsrfToken(csrfToken);
    setUser(u);
  }, []);

  const logout = useCallback(async () => {
    await apiLogout();
    setUser(null);
    setCsrfToken(null);
    await fetchCsrf();
  }, []);

  const canEdit = user?.role === "admin" || user?.role === "editor";
  const canSeeDocuments = user?.role !== "viewer";
  const isAdmin = user?.role === "admin";
  const mustChangePassword = !!user?.mustChangePassword;

  const value = useMemo(
    () => ({
      user,
      loading,
      refresh,
      login,
      register,
      logout,
      canEdit,
      canSeeDocuments,
      isAdmin,
      mustChangePassword,
    }),
    [user, loading, refresh, login, register, logout, canEdit, canSeeDocuments, isAdmin, mustChangePassword],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
